import React from 'react';
import { Phone, Linkedin, Facebook, Instagram } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { useBrand } from '../context/BrandContext';

// Sister brands across the Interetail group, organised by vertical.
// Excludes Manage Your Loans (self), the costco.manageyourloans.com.au
// subdomain (contractually exclusive — must NEVER appear in any Costco
// surface), and internal-only properties (manageyourtech.com.au).
// This block is gated by useBrand() below — only renders for the MYL
// brand, never for Costco, even if a Costco-subdomain visitor lands on
// a non-Costco page that imports this Footer.
const SISTER_BRANDS = [
  {
    heading: 'Finance & Loans',
    brands: [
      { name: 'Add Finance', href: 'https://addfinance.com.au' },
      { name: 'Empire Summit', href: 'https://empiresummit.co' },
      { name: 'Redrock Capital', href: 'https://redrockcapital.co' },
    ],
  },
  {
    heading: 'Insurance',
    brands: [
      { name: 'Add Insure', href: 'https://www.addinsure.com' },
      { name: 'Qikio Insurance', href: 'https://www.qikio.com' },
      { name: 'Australian Insurance Co', href: 'https://australianinsurance.company' },
    ],
  },
  {
    heading: 'Property & Inspections',
    brands: [
      { name: 'NT Development Group', href: 'https://www.ntdg.com.au' },
      { name: 'Australian Maintenance Co', href: 'https://www.ausmco.com.au' },
      { name: 'Aussie Inspections', href: 'https://aussieinspections.com.au' },
      { name: 'TechProp', href: 'https://techprop.com.au' },
      { name: 'Australian Resource', href: 'https://australianresource.com.au' },
    ],
  },
  {
    heading: 'Strategy & Operations',
    brands: [
      { name: 'Interetail', href: 'https://interetail.com' },
      { name: 'Kokoda Holdings', href: 'https://kokodaholdings.com' },
      { name: 'Elemental Strategies', href: 'https://elementalstrategiesgroup.com' },
      { name: 'GSD', href: 'https://gsd.company' },
      { name: 'Pexii', href: 'https://pexii.com' },
      { name: 'Peygo', href: 'https://peygo.com' },
    ],
  },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const brand = useBrand();
  const isMylBrand = brand?.id === 'myl';

  const services = [
    { name: 'First Home Buyers', href: '/first-home-buyers' },
    { name: 'Next Home Buyers', href: '/next-home' },
    { name: 'Investors', href: '/investment-property' },
    { name: 'Refinance', href: '/refinance' },
    { name: 'Commercial', href: '/commercial-loans' },
    { name: 'Asset Finance', href: '/asset-finance' },
    { name: 'Personal Loans', href: '/personal-loans' },
    { name: 'Car Loans', href: '/car-loans' },
  ];

  const company = [
    { name: 'About Us', href: '/about' },
    { name: 'Awards', href: '/awards' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Use', href: '/terms-of-use' },
  ];

  const locations = [
    { name: 'Darwin', href: '/locations/darwin' },
    { name: 'Brisbane', href: '/locations/brisbane' },
    { name: 'Melbourne', href: '/locations/melbourne' },
    { name: 'Sydney', href: '/locations/sydney' },
    { name: 'Perth', href: '/locations/perth' },
    { name: 'Adelaide', href: '/locations/adelaide' },
    { name: 'Hobart', href: '/locations/hobart' },
    { name: 'Canberra', href: '/locations/canberra' },
  ];

  const regionalLocations = [
    { name: 'Newcastle', href: '/locations/newcastle' },
    { name: 'Wollongong', href: '/locations/wollongong' },
    { name: 'Gold Coast', href: '/locations/gold-coast' },
    { name: 'Sunshine Coast', href: '/locations/sunshine-coast' },
    { name: 'Geelong', href: '/locations/geelong' },
    { name: 'Townsville', href: '/locations/townsville' },
    { name: 'Cairns', href: '/locations/cairns' },
    { name: 'Toowoomba', href: '/locations/toowoomba' },
    { name: 'Ballarat', href: '/locations/ballarat' },
    { name: 'Bendigo', href: '/locations/bendigo' },
  ];

  const resources = [
    { name: 'Support', href: '/support' },
    { name: 'Complaints Policy', href: '/complaints-policy' },
    { name: 'Calculators', href: '/calculators' },
    { name: 'Blog', href: '/blog' },
    { name: 'Technology', href: '/technology' },
    { name: "What's New", href: '/changelog' },
  ];

  return (
    <footer id="contact" className="bg-white border-t-2 border-gray-100" data-testid="footer">
      <div className="container mx-auto px-4 py-16">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Company Info */}
          <div className="min-w-0">
            <div className="mb-6">
              <span className="text-lg font-bold tracking-tight">
                <span className="text-[#00594C]">manage</span>
                {' '}
                <span className="text-[#99DF63]">your</span>
                {' '}
                <span className="text-[#00594C]">loans</span>
              </span>
            </div>
            <p className="text-gray-600 mb-6">
              Award-winning mortgage brokers helping Australians make more informed lending decisions.
            </p>
            <div className="space-y-3">
              <a
                href="tel:+61468007056"
                onClick={() => trackEvent('phone_click')}
                className="flex items-center space-x-3 text-gray-700 hover:text-[#99DF63] transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5 flex-shrink-0" />
                <span>0468 007 056</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-[#00594C]">Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href={service.href}
                    className="text-gray-600 hover:text-[#99DF63] transition-colors"
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-[#00594C]">Company</h3>
            <ul className="space-y-3">
              {company.map((item, index) => (
                <li key={index}>
                  <a
                    href={item.href}
                    className="text-gray-600 hover:text-[#99DF63] transition-colors"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-[#00594C]">Locations</h3>
            <ul className="space-y-3">
              {locations.map((location, index) => (
                <li key={index}>
                  <a
                    href={location.href}
                    className="text-gray-600 hover:text-[#99DF63] transition-colors"
                  >
                    {location.name}
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="font-semibold text-sm mt-6 mb-3 text-[#00594C]">Regional Centres</h4>
            <ul className="space-y-3">
              {regionalLocations.map((location, index) => (
                <li key={index}>
                  <a
                    href={location.href}
                    className="text-gray-600 hover:text-[#99DF63] transition-colors"
                  >
                    {location.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-bold text-lg mb-6 text-[#00594C]">Resources</h3>
            <ul className="space-y-3">
              {resources.map((resource, index) => (
                <li key={index}>
                  <a
                    href={resource.href}
                    className="text-gray-600 hover:text-[#99DF63] transition-colors"
                  >
                    {resource.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Links */}
        <div className="flex justify-center space-x-6 mb-8 pb-8 border-b border-gray-200">
          <a
            href="https://www.linkedin.com/company/manage-your-loans/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#99DF63] hover:text-white transition-all text-[#00594C]"
            data-testid="footer-linkedin"
          >
            <Linkedin className="w-5 h-5" />
          </a>
          <a
            href="https://www.facebook.com/manageyourloans.au"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#99DF63] hover:text-white transition-all text-[#00594C]"
            data-testid="footer-facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href="https://www.instagram.com/manageyourloans.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#99DF63] hover:text-white transition-all text-[#00594C]"
            data-testid="footer-instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
        </div>

        {/* Acknowledgment */}
        <div className="text-center text-sm text-gray-600 mb-6">
          <p className="mb-4">
            We acknowledge the Traditional Owners of the land where we work and live. We pay our respects to Elders past, present and emerging. We celebrate the stories, culture and traditions of Aboriginal and Torres Strait Islander Elders of all communities who also work and live on this land.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="text-center text-xs text-gray-400 mb-6 space-y-3">
          <p>
            DISCLAIMER: The information provided on this website is general in nature and is not intended to be specialist or personal advice. The information provided on our website has been prepared without taking into account your objectives or financial needs. You should consider the appropriateness of the advice to your own situation before taking any action. The information provided should not be relied upon for the purposes of entering into any legal or financial commitments. Specific investment advice should be obtained from a suitably qualified professional. If any financial product has been mentioned, you should obtain and read a copy of the relevant Product Disclosure Statement and consider the information contained within that Statement with regard to your personal circumstances before making any decision about whether to acquire the product. You can obtain a copy of the Product Disclosure Statement by speaking to a member of our team.
          </p>
          <p>
            The advice provided in marketing material and posts by Manage Your Loans, our representatives and our partners is to be considered general advice only. It has been prepared without taking into account your specific objectives, financial situation or needs. Before acting on this advice, you should consider the appropriateness of the advice having regard to your own objectives and financial needs. If any products are detailed in this presentation, you should obtain a Product Disclosure Statement relating to the products and consider the information provided before making any decisions.
          </p>
        </div>

        {/* Sister Brands — Interetail group (MYL brand only; never Costco) */}
        {isMylBrand && (
          <div className="mb-8 pb-8 border-b border-gray-200">
            <h4 className="text-center font-semibold text-sm uppercase tracking-wider text-[#00594C] mb-6">
              Part of the Interetail group
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-6">
              {SISTER_BRANDS.map((column) => (
                <div key={column.heading}>
                  <p className="text-[#00594C] text-xs font-semibold uppercase tracking-wider mb-3">
                    {column.heading}
                  </p>
                  <ul className="space-y-2">
                    {column.brands.map((b) => (
                      <li key={b.href}>
                        <a
                          href={b.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm"
                        >
                          {b.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="text-center text-sm text-gray-500 space-y-2">
          <p>
            Manage Your Loans Pty Ltd ABN 34 652 442 790 is a Credit Representative (540988) of Home Mortgage Plus Pty Ltd ACN 105 991 839 (ACL 392200)
          </p>
          <p>© {currentYear} Manage Your Loans. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;